import { useState } from "react";

const API = "https://self-service.scribeemr.com";
const RECAPTCHA_SITE_KEY = "6Ld0HvUsAAAAACBlesu0TdIZAijeAQ1p-nIRekuB";

async function getRecaptchaToken(action) {
  return new Promise((resolve) => {
    if (!window.grecaptcha) { resolve(""); return; }
    window.grecaptcha.ready(() => {
      window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action })
        .then(resolve)
        .catch(() => resolve(""));
    });
  });
}
const steps = ["Find Account", "Verify Identity", "Set Password", "Complete"];

function getStrength(pw) {
  let score = 0;
  if (pw.length >= 14) score += 25;
  if (/[A-Z]/.test(pw)) score += 20;
  if (/[a-z]/.test(pw)) score += 20;
  if (/[0-9]/.test(pw)) score += 15;
  if (/[^A-Za-z0-9]/.test(pw)) score += 20;
  return Math.min(score, 100);
}

const strengthLevels = [
  { label: "Weak", color: "#ef4444", min: 0 },
  { label: "Fair", color: "#f97316", min: 30 },
  { label: "Good", color: "#eab308", min: 55 },
  { label: "Strong", color: "#22c55e", min: 80 },
];

function getLevelForScore(score) {
  return [...strengthLevels].reverse().find((l) => score >= l.min) || strengthLevels[0];
}

// ─── Admin Dashboard ─────────────────────────────────────────────────────────
function AdminDashboard({ onLogout }) {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("");
  const [resetUser, setResetUser] = useState({ username: "", loading: false, msg: "" });

  async function fetchData() {
    setLoading(true);
    try {
      const [logsRes, statsRes] = await Promise.all([
        fetch(`${API}/api/admin/logs?lines=100`),
        fetch(`${API}/api/admin/stats`),
      ]);
      setLogs((await logsRes.json()).logs || []);
      setStats(await statsRes.json());
    } catch (e) {
      alert("Failed: " + e.message);
    } finally {
      setLoading(false);
    }
  }

  useState(() => { fetchData(); }, []);

  async function handleResetUserOTP() {
    if (!resetUser.username) return;
    setResetUser(p => ({ ...p, loading: true }));
    try {
      const res = await fetch(`${API}/api/admin/reset-user-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: resetUser.username }),
      });
      const data = await res.json();
      setResetUser(p => ({ ...p, loading: false, msg: `✓ Rate limit reset for ${resetUser.username}` }));
    } catch (e) {
      setResetUser(p => ({ ...p, loading: false, msg: "Failed: " + e.message }));
    }
  }

  const filteredLogs = logs.filter(l => {
    const matchSearch = !search || l.toLowerCase().includes(search.toLowerCase());
    const matchFilter = !filter || l.includes(filter);
    return matchSearch && matchFilter;
  }).reverse();

  function getBadge(log) {
    if (log.includes("RESET_SUCCESS")) return { label: "Reset Success", color: "#166534", bg: "#dcfce7" };
    if (log.includes("OTP_SENT")) return { label: "OTP Sent", color: "#1e40af", bg: "#dbeafe" };
    if (log.includes("RATE_LIMIT")) return { label: "Rate Limited", color: "#991b1b", bg: "#fee2e2" };
    if (log.includes("RESET_FAILED")) return { label: "Failed", color: "#92400e", bg: "#fef3c7" };
    if (log.includes("WRONG_OTP")) return { label: "Wrong OTP", color: "#991b1b", bg: "#fee2e2" };
    if (log.includes("ADMIN")) return { label: "Admin", color: "#1e40af", bg: "#dbeafe" };
    if (log.includes("LOOKUP")) return { label: "Lookup", color: "#0369a1", bg: "#e0f2fe" };
    return { label: "Info", color: "#374151", bg: "#f3f4f6" };
  }

  function parseLog(log) {
    const parts = log.split(" | ");
    return { time: parts[0] || "", message: parts.slice(2).join(" | ") || log };
  }

  return (
    <div style={ad.root}>
      <div style={ad.header}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <img src="/images/scribe-emr-logo.png" alt="ScribeEMR" style={{ height: 36, objectFit: "contain" }} onError={e => e.target.style.display = "none"} />
          <div>
            <h1 style={ad.title}>Admin Dashboard</h1>
            <p style={ad.subtitle}>AD Reset Portal · Audit & Monitoring</p>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={ad.refreshBtn} onClick={fetchData} disabled={loading}>{loading ? "Loading..." : "↻ Refresh"}</button>
          <button style={ad.logoutBtn} onClick={onLogout}>Logout</button>
        </div>
      </div>

      <div style={ad.body}>
        {stats && (
          <div style={ad.metrics}>
            {[
              { label: "Resets today", value: stats.resets_today, color: "#166534", bg: "#dcfce7" },
              { label: "OTPs sent", value: stats.otps_sent, color: "#1e40af", bg: "#dbeafe" },
              { label: "Rate limit hits", value: stats.rate_limit_hits, color: "#991b1b", bg: "#fee2e2" },
              { label: "Failed attempts", value: stats.failed_attempts, color: "#92400e", bg: "#fef3c7" },
              { label: "Wrong OTP", value: stats.wrong_otp_attempts, color: "#991b1b", bg: "#fee2e2" },
            ].map((m, i) => (
              <div key={i} style={{ ...ad.metric, background: m.bg }}>
                <p style={{ ...ad.metricLabel, color: m.color }}>{m.label}</p>
                <p style={{ ...ad.metricValue, color: m.color }}>{m.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Reset user rate limit */}
        <div style={{ ...ad.section, marginBottom: 16 }}>
          <p style={ad.sectionTitle}>🔄 Reset User Rate Limit</p>
          <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>Reset OTP rate limit for users who are blocked.</p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input style={{ ...ad.searchInput, flex: 1 }} placeholder="Enter AD username..."
              value={resetUser.username} onChange={e => setResetUser(p => ({ ...p, username: e.target.value, msg: "" }))} />
            <button style={{ ...ad.refreshBtn, background: "#e0f2fe", color: "#0284c7" }}
              onClick={handleResetUserOTP} disabled={resetUser.loading || !resetUser.username}>
              {resetUser.loading ? "Resetting..." : "Reset Limit"}
            </button>
          </div>
          {resetUser.msg && <p style={{ fontSize: 12, color: resetUser.msg.startsWith("✓") ? "#166534" : "#991b1b", marginTop: 8 }}>{resetUser.msg}</p>}
        </div>

        {/* Audit Log */}
        <div style={ad.section}>
          <p style={ad.sectionTitle}>Audit Log</p>
          <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
            <input style={ad.searchInput} placeholder="Search user, IP, event..."
              value={search} onChange={e => setSearch(e.target.value)} />
            <select style={ad.select} value={filter} onChange={e => setFilter(e.target.value)}>
              <option value="">All events</option>
              <option value="RESET_SUCCESS">Reset success</option>
              <option value="OTP_SENT">OTP sent</option>
              <option value="RATE_LIMIT">Rate limited</option>
              <option value="WRONG_OTP">Wrong OTP</option>
              <option value="ADMIN">Admin login</option>
            </select>
          </div>
          {filteredLogs.length === 0 ? (
            <p style={{ color: "#6b7280", fontSize: 13, textAlign: "center", padding: "32px 0" }}>
              {logs.length === 0 ? "No logs yet" : "No matching logs"}
            </p>
          ) : (
            <table style={ad.table}>
              <thead>
                <tr>
                  <th style={ad.th}>Time</th>
                  <th style={ad.th}>Event</th>
                  <th style={ad.th}>Details</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.map((log, i) => {
                  const parsed = parseLog(log);
                  const badge = getBadge(log);
                  return (
                    <tr key={i} style={{ borderBottom: "1px solid #e0f2fe" }}>
                      <td style={{ ...ad.td, fontFamily: "monospace", fontSize: 11, color: "#6b7280", whiteSpace: "nowrap" }}>{parsed.time}</td>
                      <td style={ad.td}>
                        <span style={{ background: badge.bg, color: badge.color, fontSize: 11, padding: "3px 8px", borderRadius: 20, fontWeight: 600 }}>{badge.label}</span>
                      </td>
                      <td style={{ ...ad.td, color: "#374151", fontSize: 12 }}>{parsed.message}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          <p style={{ fontSize: 11, color: "#9ca3af", marginTop: 10 }}>Total: {logs.length} · Showing: {filteredLogs.length}</p>
        </div>
      </div>
    </div>
  );
}

// ─── Admin Login (Password based) ────────────────────────────────────────────
function AdminLogin({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  function handleLogin() {
    if (username === "admin" && password === "Admin@ScribeEMR123!") {
      onLogin();
    } else {
      setError("Invalid username or password");
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f0f9ff", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "system-ui, sans-serif" }}>
      <div style={{ width: 380, background: "#fff", border: "1px solid #bae6fd", borderRadius: 16, padding: 32, boxShadow: "0 4px 24px rgba(14,165,233,0.1)" }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <img src="/images/scribe-emr-logo.png" alt="ScribeEMR" style={{ height: 40, objectFit: "contain", marginBottom: 16 }} onError={e => e.target.style.display = "none"} />
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#0c4a6e" }}>Admin Login</h1>
          <p style={{ margin: "4px 0 0", fontSize: 12, color: "#0369a1" }}>IT Team Only</p>
        </div>
        <label style={s.label}>Username</label>
        <input style={s.input} placeholder="admin" value={username}
          onChange={e => setUsername(e.target.value)} />
        <label style={{ ...s.label, marginTop: 12 }}>Password</label>
        <input style={s.input} type="password" placeholder="••••••••" value={password}
          onChange={e => setPassword(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleLogin()} />
        {error && <p style={{ color: "#ef4444", fontSize: 12, margin: "6px 0" }}>{error}</p>}
        <button style={{ ...s.btn, marginTop: 16 }} onClick={handleLogin}>Login →</button>
      </div>
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
function checkAdmin() {
  return window.location.pathname === "/admin";
}

export default function App() {
  const [sessionToken, setSessionToken] = useState("");
  const [view, setView] = useState(checkAdmin() ? "adminLogin" : "portal");
  const [adminLoggedIn, setAdminLoggedIn] = useState(false);
  const [step, setStep] = useState(0);
  const [username, setUsername] = useState("");
  const [userInfo, setUserInfo] = useState(null);
  const [userError, setUserError] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [pwError, setPwError] = useState("");
  const [loading, setLoading] = useState(false);

  const score = getStrength(newPw);
  const level = getLevelForScore(score);
  const pwValid = newPw.length >= 14 && /[A-Z]/.test(newPw) && /[a-z]/.test(newPw) && /[0-9]/.test(newPw) && /[^A-Za-z0-9]/.test(newPw);

  async function handleLookup() {
    setUserError(""); setLoading(true);
    try {
      const recaptcha_token = await getRecaptchaToken("lookup");
      const res = await fetch(`${API}/api/lookup`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ username, recaptcha_token }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "User not found");
      setUserInfo(data); setStep(1);
    } catch (e) { setUserError(e.message); } finally { setLoading(false); }
  }

  async function handleSendOTP() {
    setLoading(true); setOtpError("");
    try {
      const recaptcha_token = await getRecaptchaToken("send_otp");
      const res = await fetch(`${API}/api/send-otp`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ username, recaptcha_token }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to send OTP");
      setOtpSent(true);
    } catch (e) { setOtpError(e.message); } finally { setLoading(false); }
  }

  async function handleVerifyOTP() {
    setOtpError(""); setLoading(true);
    try {
      const res = await fetch(`${API}/api/verify-otp`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ username, otp }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Invalid code");
      setSessionToken(data.session_token);
      setStep(2);
    } catch (e) { setOtpError(e.message); } finally { setLoading(false); }
  }

  async function handleReset() {
    setPwError("");
    if (!pwValid) { setPwError("Password does not meet requirements."); return; }
    if (newPw !== confirmPw) { setPwError("Passwords do not match."); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/reset-password`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ username, session_token: sessionToken, new_password: newPw }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Reset failed");
      setStep(3);
    } catch (e) { setPwError(e.message); } finally { setLoading(false); }
  }

  function resetAll() {
    setStep(0); setUsername(""); setUserInfo(null); setUserError("");
    setOtp(""); setOtpSent(false); setOtpError("");
    setNewPw(""); setConfirmPw(""); setPwError(""); setLoading(false); setSessionToken("");
  }

  if (view === "adminLogin") return <AdminLogin onLogin={() => { setAdminLoggedIn(true); setView("admin"); }} />;
  if (view === "admin" && adminLoggedIn) return <AdminDashboard onLogout={() => { setAdminLoggedIn(false); setView("portal"); }} />;

  return (
    <div style={s.root}>
      <div style={s.bgPattern} />
      <div style={s.card}>
        <div style={s.header}>
          <img src="/images/scribe-emr-logo.png" alt="ScribeEMR" style={{ height: 38, objectFit: "contain" }} onError={e => e.target.style.display = "none"} />
          <div>
            <h1 style={s.title}>AD Password Reset</h1>
            <p style={s.subtitle}>Self-Service Portal</p>
          </div>
        </div>

        <div style={s.stepTrack}>
          {steps.map((label, i) => (
            <div key={i} style={s.stepItem}>
              <div style={{ ...s.stepDot, background: i < step ? "#0284c7" : i === step ? "#0ea5e9" : "#e0f2fe", border: `2px solid ${i < step ? "#0284c7" : i === step ? "#0ea5e9" : "#bae6fd"}` }}>
                {i < step
                  ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>
                  : <span style={{ fontSize: 11, fontWeight: 700, color: i === step ? "#fff" : "#0284c7" }}>{i + 1}</span>}
              </div>
              <span style={{ ...s.stepLabel, color: i === step ? "#0c4a6e" : "#7dd3fc" }}>{label}</span>
              {i < steps.length - 1 && <div style={{ ...s.stepLine, background: i < step ? "#0284c7" : "#bae6fd" }} />}
            </div>
          ))}
        </div>

        {/* Step 0 */}
        {step === 0 && (
          <div style={s.panel}>
            <h2 style={s.panelTitle}>Enter Your Username</h2>
            <p style={s.panelDesc}>Enter your AD username to begin the password reset process.</p>
            <label style={s.label}>AD Username</label>
            <div style={s.inputRow}>
              
              <input style={s.inputFlat} placeholder="username" value={username}
                onChange={e => setUsername(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleLookup()} autoFocus />
            </div>
            {userError && <p style={s.error}>{userError}</p>}
            <button style={s.btn} onClick={handleLookup} disabled={loading || !username}>
              {loading ? "Looking up..." : "Find Account →"}
            </button>
          </div>
        )}

        {/* Step 1 */}
        {step === 1 && userInfo && (
          <div style={s.panel}>
            <h2 style={s.panelTitle}>Verify Your Identity</h2>
            <div style={s.userCard}>
              <div style={s.avatar}>{userInfo.name.charAt(0).toUpperCase()}</div>
              <div>
                <p style={s.userName}>{userInfo.name}</p>
                <p style={s.userMeta}>{userInfo.email}</p>
              </div>
            </div>
            {!otpSent ? (
              <>
                <p style={s.panelDesc}>OTP will be sent to your registered email address.</p>
                {otpError && <p style={s.error}>{otpError}</p>}
                <button style={s.btn} onClick={handleSendOTP} disabled={loading}>
                  {loading ? "Sending..." : "Send OTP →"}
                </button>
              </>
            ) : (
              <>
                <label style={s.label}>Enter OTP</label>
                <p style={{ ...s.panelDesc, marginBottom: 8 }}>Check your email for the 6-digit code.</p>
                <input style={{ ...s.input, letterSpacing: 10, fontSize: 22, textAlign: "center" }}
                  placeholder="------" maxLength={6} value={otp}
                  onChange={e => setOtp(e.target.value.replace(/\D/g, ""))} />
                {otpError && <p style={s.error}>{otpError}</p>}
                <button style={s.btn} onClick={handleVerifyOTP} disabled={loading || otp.length !== 6}>
                  {loading ? "Verifying..." : "Verify OTP →"}
                </button>
                <button style={s.ghostBtn} onClick={() => { setOtpSent(false); setOtp(""); setOtpError(""); }}>Resend OTP</button>
              </>
            )}
          </div>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <div style={s.panel}>
            <h2 style={s.panelTitle}>Set New Password</h2>
            <div style={s.policyBox}>
              <p style={s.policyTitle}>Password Requirements</p>
              {[
                { label: "At least 14 characters", met: newPw.length >= 14 },
                { label: "One uppercase letter (A-Z)", met: /[A-Z]/.test(newPw) },
                { label: "One lowercase letter (a-z)", met: /[a-z]/.test(newPw) },
                { label: "One number (0-9)", met: /[0-9]/.test(newPw) },
                { label: "One special character (!@#$...)", met: /[^A-Za-z0-9]/.test(newPw) },
              ].map((p, i) => (
                <div key={i} style={s.policyRow}>
                  <span style={{ color: p.met ? "#0284c7" : "#9ca3af", fontSize: 12 }}>{p.met ? "✓" : "○"}</span>
                  <span style={{ color: p.met ? "#0c4a6e" : "#6b7280", fontSize: 12 }}>{p.label}</span>
                </div>
              ))}
            </div>
            <label style={s.label}>New Password</label>
            <div style={{ position: "relative" }}>
              <input style={{ ...s.input, paddingRight: 44 }} type={showPw ? "text" : "password"}
                placeholder="Enter new password (min 14 chars)" value={newPw} onChange={e => setNewPw(e.target.value)} />
              <button onClick={() => setShowPw(p => !p)} style={s.eyeBtn}>{showPw ? "🙈" : "👁️"}</button>
            </div>
            {newPw && (
              <div style={{ marginTop: 8 }}>
                <div style={s.strengthTrack}><div style={{ ...s.strengthFill, width: `${score}%`, background: level.color }} /></div>
                <p style={{ color: level.color, fontSize: 11, marginTop: 4, fontWeight: 700 }}>{level.label}</p>
              </div>
            )}
            <label style={{ ...s.label, marginTop: 14 }}>Confirm Password</label>
            <input style={{ ...s.input, borderColor: confirmPw && confirmPw !== newPw ? "#ef4444" : undefined }}
              type={showPw ? "text" : "password"} placeholder="Re-enter password"
              value={confirmPw} onChange={e => setConfirmPw(e.target.value)} />
            {confirmPw && confirmPw !== newPw && <p style={s.error}>Passwords do not match</p>}
            {pwError && <p style={s.error}>{pwError}</p>}
            <button style={{ ...s.btn, opacity: pwValid && confirmPw === newPw ? 1 : 0.5 }}
              onClick={handleReset} disabled={loading || !pwValid || newPw !== confirmPw}>
              {loading ? "Resetting..." : "Reset Password →"}
            </button>
          </div>
        )}

        {/* Step 3 */}
        {step === 3 && (
          <div style={{ ...s.panel, textAlign: "center", padding: "48px 32px" }}>
            <div style={s.successIcon}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>
            </div>
            <h2 style={{ ...s.panelTitle, marginBottom: 8 }}>Password Reset Complete!</h2>
            <p style={{ color: "#6b7280", fontSize: 14, lineHeight: 1.6, marginBottom: 28 }}>
              Your Active Directory password has been successfully updated.
              You can now log in with your new password.
            </p>
            <div style={s.infoBox}>
              <div style={s.infoRow}><span style={s.infoKey}>Account</span><span style={s.infoVal}>{username}</span></div>
              <div style={s.infoRow}><span style={s.infoKey}>Status</span><span style={{ ...s.infoVal, color: "#0284c7" }}>● Active</span></div>
              <div style={{ ...s.infoRow, borderBottom: "none" }}><span style={s.infoKey}>Reset at</span><span style={s.infoVal}>{new Date().toLocaleString()}</span></div>
            </div>
            <button style={{ ...s.btn, background: "#e0f2fe", color: "#0284c7", marginTop: 24 }} onClick={resetAll}>
              Reset Another Account
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  root: { minHeight: "100vh", background: "#f0f9ff", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "24px 16px", fontFamily: "system-ui, -apple-system, sans-serif", position: "relative" },
  bgPattern: { position: "fixed", inset: 0, backgroundImage: "radial-gradient(circle at 1px 1px, #bae6fd 1px, transparent 0)", backgroundSize: "32px 32px", pointerEvents: "none", opacity: 0.5 },
  card: { width: "100%", maxWidth: 520, background: "#fff", border: "1px solid #bae6fd", borderRadius: 16, overflow: "hidden", boxShadow: "0 4px 32px rgba(14,165,233,0.1)", position: "relative", zIndex: 1 },
  header: { display: "flex", alignItems: "center", gap: 14, padding: "20px 28px", borderBottom: "1px solid #e0f2fe", background: "#f0f9ff" },
  title: { margin: 0, fontSize: 16, fontWeight: 700, color: "#0c4a6e" },
  subtitle: { margin: "2px 0 0", fontSize: 11, color: "#0369a1", letterSpacing: "0.08em", textTransform: "uppercase" },
  stepTrack: { display: "flex", alignItems: "center", padding: "20px 28px 28px", background: "#fff" },
  stepItem: { display: "flex", alignItems: "center", flex: 1, position: "relative" },
  stepDot: { width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, zIndex: 1 },
  stepLabel: { fontSize: 10, position: "absolute", top: 34, left: "50%", transform: "translateX(-50%)", whiteSpace: "nowrap", letterSpacing: "0.04em", textTransform: "uppercase" },
  stepLine: { flex: 1, height: 2, marginLeft: 4, marginRight: 4 },
  panel: { padding: "24px 28px 28px", background: "#fff" },
  panelTitle: { margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: "#0c4a6e" },
  panelDesc: { margin: "0 0 18px", fontSize: 13, color: "#6b7280", lineHeight: 1.5 },
  label: { display: "block", fontSize: 11, color: "#0284c7", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8, fontWeight: 600 },
  inputRow: { display: "flex", alignItems: "center", background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 8, overflow: "hidden", marginBottom: 8 },
  prefix: { padding: "0 10px", fontSize: 11, color: "#0369a1", borderRight: "1px solid #bae6fd", height: 44, display: "flex", alignItems: "center", background: "#e0f2fe", flexShrink: 0, fontWeight: 600 },
  inputFlat: { flex: 1, background: "transparent", border: "none", padding: "0 14px", height: 44, color: "#0c4a6e", fontSize: 14, fontFamily: "inherit", outline: "none" },
  input: { width: "100%", background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 8, padding: "0 14px", height: 44, color: "#0c4a6e", fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box", marginBottom: 8 },
  btn: { display: "block", width: "100%", marginTop: 16, padding: "13px 0", background: "#0284c7", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" },
  ghostBtn: { display: "block", width: "100%", marginTop: 8, padding: "11px 0", background: "transparent", color: "#0369a1", border: "1px solid #bae6fd", borderRadius: 8, fontSize: 12, fontFamily: "inherit", cursor: "pointer" },
  error: { color: "#ef4444", fontSize: 12, margin: "4px 0 8px" },
  userCard: { display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 10, marginBottom: 18 },
  avatar: { width: 40, height: 40, borderRadius: "50%", background: "#0284c7", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 16, fontWeight: 700, flexShrink: 0 },
  userName: { margin: 0, fontSize: 14, fontWeight: 700, color: "#0c4a6e" },
  userMeta: { margin: "2px 0 0", fontSize: 11, color: "#0369a1" },
  policyBox: { background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 10, padding: "14px 16px", marginBottom: 18 },
  policyTitle: { margin: "0 0 10px", fontSize: 10, color: "#0369a1", letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 600 },
  policyRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 4 },
  strengthTrack: { height: 4, background: "#e0f2fe", borderRadius: 2, overflow: "hidden" },
  strengthFill: { height: "100%", borderRadius: 2, transition: "width 0.3s,background 0.3s" },
  eyeBtn: { position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", fontSize: 16, padding: 4 },
  successIcon: { width: 72, height: 72, borderRadius: "50%", background: "#e0f2fe", border: "2px solid #7dd3fc", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" },
  infoBox: { background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 10, padding: "16px 20px", textAlign: "left" },
  infoRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #e0f2fe" },
  infoKey: { fontSize: 11, color: "#6b7280", letterSpacing: "0.06em", textTransform: "uppercase" },
  infoVal: { fontSize: 12, color: "#0c4a6e", fontWeight: 600 },
  adminLink: { marginTop: 16, fontSize: 11, color: "#0369a1", background: "none", border: "none", cursor: "pointer", letterSpacing: "0.06em", textTransform: "uppercase", position: "relative", zIndex: 1 },
};

const ad = {
  root: { minHeight: "100vh", background: "#f0f9ff", fontFamily: "system-ui, sans-serif" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 24px", background: "#fff", borderBottom: "1px solid #bae6fd" },
  title: { margin: 0, fontSize: 16, fontWeight: 700, color: "#0c4a6e" },
  subtitle: { margin: "2px 0 0", fontSize: 11, color: "#0369a1", textTransform: "uppercase", letterSpacing: "0.08em" },
  refreshBtn: { fontSize: 12, color: "#0284c7", background: "#e0f2fe", border: "1px solid #bae6fd", borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontFamily: "inherit" },
  logoutBtn: { fontSize: 12, color: "#ef4444", background: "#fee2e2", border: "1px solid #fecaca", borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontFamily: "inherit" },
  body: { maxWidth: 1100, margin: "0 auto", padding: "24px" },
  metrics: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12, marginBottom: 16 },
  metric: { borderRadius: 10, padding: "16px 20px" },
  metricLabel: { margin: "0 0 8px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 },
  metricValue: { margin: 0, fontSize: 28, fontWeight: 700 },
  section: { background: "#fff", border: "1px solid #bae6fd", borderRadius: 12, padding: "20px 24px" },
  sectionTitle: { margin: "0 0 14px", fontSize: 14, fontWeight: 700, color: "#0c4a6e" },
  searchInput: { flex: 1, minWidth: 200, background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 8, padding: "0 12px", height: 36, color: "#0c4a6e", fontSize: 13, fontFamily: "inherit", outline: "none" },
  select: { background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 8, padding: "0 12px", height: 36, color: "#0c4a6e", fontSize: 13, fontFamily: "inherit", outline: "none" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { textAlign: "left", fontSize: 11, color: "#6b7280", padding: "8px 12px", borderBottom: "1px solid #e0f2fe", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 },
  td: { padding: "10px 12px", color: "#0c4a6e", fontSize: 13 },
};
